import {testIdProps} from '@github-ui/test-id-props'
import {DuplicateIcon} from '@primer/octicons-react'
import {ActionList, Tooltip} from '@primer/react'
import {useCallback} from 'react'

import type {ClientMemexWorkflow} from '../../api/workflows/contracts'
import {useNewWorkflowDialog, useWorkflows} from '../../state-providers/workflows/use-workflows'
import {CreateWorkflowResources} from '../../strings'

type DuplicateWorkflowButtonProps = {
  workflowToDuplicate: ClientMemexWorkflow
}

export const DuplicateWorkflowButton = ({workflowToDuplicate}: DuplicateWorkflowButtonProps) => {
  const {setIsDialogOpen, applyWorkflowTemplate} = useNewWorkflowDialog()
  const {customWorkflowLimitReached, setWorkflowMenuState} = useWorkflows()

  const onSelectHandler = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!customWorkflowLimitReached) {
        applyWorkflowTemplate(workflowToDuplicate)
        setIsDialogOpen(true)
        setWorkflowMenuState(workflowToDuplicate.clientId)
        e.stopPropagation()
      }
    },
    [customWorkflowLimitReached, applyWorkflowTemplate, workflowToDuplicate, setIsDialogOpen, setWorkflowMenuState],
  )

  return customWorkflowLimitReached ? (
    <Tooltip aria-label={CreateWorkflowResources.userAddedWorkflowLimitReached} direction="s" wrap>
      <ActionList.Item
        as="button"
        key={`duplicate-option-${workflowToDuplicate.clientId}`}
        aria-label="Duplicate workflow"
        onSelect={(e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation()}
        {...testIdProps(`duplicate-option-disabled-${workflowToDuplicate.clientId}`)}
        disabled
      >
        <ActionList.LeadingVisual>
          <DuplicateIcon className="duplicate-workflow-button" />
        </ActionList.LeadingVisual>
        <span className="duplicate-workflow-button">{CreateWorkflowResources.duplicateWorkflowLabel}</span>
      </ActionList.Item>
    </Tooltip>
  ) : (
    <ActionList.Item
      as="button"
      key={`duplicate-option-${workflowToDuplicate.clientId}`}
      onSelect={onSelectHandler}
      aria-label="Duplicate workflow"
      {...testIdProps(`duplicate-option-${workflowToDuplicate.clientId}`)}
    >
      <ActionList.LeadingVisual>
        <DuplicateIcon className="duplicate-workflow-button" />
      </ActionList.LeadingVisual>
      <span className="duplicate-workflow-button">{CreateWorkflowResources.duplicateWorkflowLabel}</span>
    </ActionList.Item>
  )
}

try{ DuplicateWorkflowButton.displayName ||= 'DuplicateWorkflowButton' } catch {}